import React, { useEffect, useMemo, useState } from "react";
import "./style.css";
import { Header, Footer, Seo } from "../../components";
import { service } from "../../store";
import { CoursesHero } from "./CoursesHero";
import { CoursesFilter } from "./CoursesFilter";
import { CoursesListCard } from "./CoursesListCard";
import { CoursesListCardSkeleton } from "./CoursesListCardSkeleton";
import { matchesCourseFilter } from "./coursesConfig";
import { coursePathById } from "./courseSlugMap";
import { stripHtmlToText } from "../FAQ/faqPageUtils";
import { SITE_URL, SITE_NAME, OG_IMAGE, canonicalUrl } from "../../utils/seo";

const SKELETON_COUNT = 6;

export const Courses = () => {
  const getServicesbyGroup = service((state) => state.getServicesbyGroup);
  const isLoading = service((state) => state.isLoading);
  const [courses, setCourses] = useState([]);
  const [activeFilter, setActiveFilter] = useState("all");

  useEffect(() => {
    const fetchServices = async () => {
      const [lectureServices, normalServices] = await Promise.all([
        getServicesbyGroup(1),
        getServicesbyGroup(2),
      ]);

      const merged = [
        ...(Array.isArray(lectureServices) ? lectureServices : []),
        ...(Array.isArray(normalServices) ? normalServices : []),
      ];

      const uniqueById = merged.filter(
        (course, index, list) =>
          course.is_active === true &&
          list.findIndex((item) => item.id === course.id) === index
      );

      setCourses(uniqueById);
    };

    fetchServices();
  }, [getServicesbyGroup]);

  const filteredCourses = useMemo(
    () => courses.filter((course) => matchesCourseFilter(course, activeFilter)),
    [courses, activeFilter]
  );

  // 교육과정 목록 ItemList 구조화 데이터: 화면에 노출되는 공개 과정을 순서대로 동적 생성.
  const itemListJsonLd = useMemo(() => {
    if (!courses.length) return undefined;
    return {
      "@context": "https://schema.org",
      "@type": "ItemList",
      "@id": `${SITE_URL}/courses#course-list`,
      name: "희망과학 온라인 양형교육 과정",
      url: `${SITE_URL}/courses`,
      numberOfItems: courses.length,
      itemListElement: courses.map((course, index) => {
        const url = `${SITE_URL}${coursePathById(course.id)}`;
        const plain = stripHtmlToText(course.description);
        const description = plain
          ? `${plain.slice(0, 155)}${plain.length > 155 ? "…" : ""}`
          : undefined;
        return {
          "@type": "ListItem",
          position: index + 1,
          url,
          item: {
            "@type": "Course",
            "@id": `${url}#course`,
            url,
            name: course.title,
            ...(description ? { description } : {}),
            provider: {
              "@type": "Organization",
              "@id": `${SITE_URL}/#organization`,
              name: SITE_NAME,
              url: `${SITE_URL}/`,
            },
          },
        };
      }),
    };
  }, [courses]);

  return (
    <>
      <Seo
        title="온라인 양형교육 과정 안내｜희망과학 심리상담센터"
        description="성범죄·음주운전 재범방지교육과 양형기준의 이해 등 온라인 양형교육 과정을 확인하세요. 과정별 교육 내용과 신청 방법을 안내합니다."
        ogTitle="온라인 양형교육 과정 안내｜희망과학"
        ogDescription="상황에 맞는 재범방지교육과 양형교육 과정의 내용 및 신청 방법을 확인하세요."
        image={OG_IMAGE}
        canonical={canonicalUrl("/courses")}
        jsonLd={itemListJsonLd}
      />
      <div className="courses-page-shell">
        <Header variant="dark" />
        <main className="courses-page">
          <CoursesHero />
          <section className="courses-page__content" aria-label="교육 과정 목록">
            <div className="courses-page__inner">
              <CoursesFilter
                activeFilter={activeFilter}
                onFilterChange={setActiveFilter}
              />
              <div
                className={`courses-page__grid${
                  isLoading ? " courses-page__grid--loading" : ""
                }`}
              >
                {isLoading ? (
                  Array.from({ length: SKELETON_COUNT }).map((_, index) => (
                    <CoursesListCardSkeleton key={index} />
                  ))
                ) : filteredCourses.length > 0 ? (
                  filteredCourses.map((course) => (
                    <CoursesListCard key={course.id} course={course} />
                  ))
                ) : (
                  <p className="courses-page__empty" role="status">
                    해당 카테고리에 등록된 교육 과정이 없습니다.
                  </p>
                )}
              </div>
            </div>
          </section>
        </main>
      </div>
      <Footer />
    </>
  );
};
